import { useAuth } from './auth/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLoginEvent } from '../hooks/api/useLoginEvent';
import { useUsers } from '../hooks/api/useUsers';

interface User {
  [key: string]: any;
}

interface AuthContextType {
  user: User | undefined;
  account: string;
  setAccount: (account: string | null) => void;
  accountList: Record<string, string>;
  loggedInAccount: string;
  isImpersonating: boolean;
  impersonationLock: boolean;
  setImpersonationLock: (lock: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: undefined,
  account: '',
  setAccount: () => {},
  accountList: {},
  loggedInAccount: '',
  isImpersonating: false,
  impersonationLock: true,
  setImpersonationLock: () => {},
});

export function useAuthContext() {
  return useContext(AuthContext);
}

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const { accounts: accountList } = useUsers();
  const loginEvent = useLoginEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const isFromLogin = searchParams.has('fromLogin');
  const queryClient = useQueryClient();
  const [impersonationLock, setImpersonationLock] = useState(true);

  useEffect(() => {
    if (isFromLogin && sessionStorage.getItem('loginEventHandled') !== 'true') {
      loginEvent.mutate();
      searchParams.delete('fromLogin');
      setSearchParams(searchParams);

      sessionStorage.setItem('loginEventHandled', 'true');
    }
  }, [isFromLogin, loginEvent, searchParams, setSearchParams]);

  const currentAccount =
    user?.[`https://api.jstscifree.com/app_metadata`]?.account;
  // undefined until an account is chosen; null once the selection is cleared.
  const [account, setAccount] = useState<string | null | undefined>(undefined);
  // Derived, not set in an effect: an effect leaves them empty for the first
  // render, and AuthGuard's adminOnly / requireSelectedAccount checks then
  // redirect a reload or deep link home.
  const loggedInAccount: string = currentAccount ?? '';
  const savedAccount = localStorage.getItem('account');
  const effectiveAccount =
    account === undefined ? savedAccount || currentAccount || null : account;

  // Also when a different user signs in without a page reload: what's saved
  // then belongs to them (AuthProvider clears the previous user's).
  useEffect(() => {
    setAccount(savedAccount || currentAccount);
    setImpersonationLock(localStorage.getItem('impersonationLock') === 'true');
  }, [currentAccount]);

  useEffect(() => {
    if (effectiveAccount) {
      localStorage.setItem('account', effectiveAccount);
    }
    queryClient.invalidateQueries();
  }, [effectiveAccount, queryClient]);

  const impersonationLockChangeHandler = useCallback(
    (impersonationLockEnabled: boolean) => {
      setImpersonationLock(impersonationLockEnabled);
      localStorage.setItem(
        'impersonationLock',
        impersonationLockEnabled.toString()
      );
    },
    []
  );

  const setAccountHandler = useCallback(
    (selectedAccount: string | null) => {
      setAccount(selectedAccount);
      const impersonationLockEnabled =
        selectedAccount !== loggedInAccount && loggedInAccount === 'admin';
      setImpersonationLock(impersonationLockEnabled);
      localStorage.setItem(
        'impersonationLock',
        impersonationLockEnabled.toString()
      );
      if (selectedAccount) {
        localStorage.setItem('account', selectedAccount);
      } else {
        localStorage.removeItem('account');
      }
    },
    [loggedInAccount]
  );

  const isImpersonating = useMemo(
    () => effectiveAccount != null && loggedInAccount !== effectiveAccount,
    [loggedInAccount, effectiveAccount]
  );
  const contextValue: AuthContextType = {
    user,
    account: effectiveAccount || '',
    setAccount: setAccountHandler,
    accountList,
    loggedInAccount,
    isImpersonating,
    impersonationLock: impersonationLock && isImpersonating,
    setImpersonationLock: impersonationLockChangeHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
